<template>
    <div class="code-input-wrapper">
        <div
            class="code-input-group"
            :class="{
                'is-shaking': isShaking,
                'is-success': isSuccess,
                'is-hidden': hidden
            }"
        >
            <template v-for="(digit, index) in digits">
                <input
                    :key="`input-${index}`"
                    :ref="`codeInput${index}`"
                    v-model="digits[index]"
                    type="text"
                    class="code-input"
                    :inputmode="inputMode"
                    :pattern="inputPattern"
                    maxlength="1"
                    autocomplete="one-time-code"
                    :disabled="disabled"
                    :aria-label="`Code digit ${index + 1}`"
                    @input="handleInput(index, $event)"
                    @keydown="handleKeydown(index, $event)"
                    @paste="handlePaste"
                    @focus="handleFocus(index)"
                />

                <!-- Optional separator -->
                <div
                    v-if="calculatedSeparatorPosition && index === calculatedSeparatorPosition - 1"
                    :key="`separator-${index}`"
                    class="code-input-separator"
                >
                    {{ separator }}
                </div>
            </template>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CodeInput',

    props: {
        // Number of digits in the code
        length: {
            type: Number,
            default: 6,
            validator: (value) => value >= 4 && value <= 10
        },

        // Type of input: 'numeric', 'alphanumeric' or 'alphanumeric_uppercase'
        type: {
            type: String,
            default: 'numeric',
            validator: (value) => ['numeric', 'alphanumeric', 'alphanumeric_uppercase'].includes(value)
        },

        // Disabled state
        disabled: {
            type: Boolean,
            default: false
        },

        // Position to add separator (e.g., 3 will add separator after 3rd digit)
        separatorPosition: {
            type: Number,
            default: null,
            validator: (value) => value === null || (value > 0 && value < 10)
        },

        // Separator character
        separator: {
            type: String,
            default: '-'
        },

        // Auto-submit when code is complete
        autoSubmit: {
            type: Boolean,
            default: false
        },

        // Hidden state (for animations)
        hidden: {
            type: Boolean,
            default: false
        },

        // Initial value
        value: {
            type: String,
            default: ''
        }
    },

    data() {
        return {
            digits: [],
            isShaking: false,
            isSuccess: false,
            shakeTimeout: null,
            successTimeout: null
        };
    },

    computed: {
        inputMode() {
            return this.type === 'numeric' ? 'numeric' : 'text';
        },

        inputPattern() {
            return this.type === 'numeric' ? '[0-9]*' : '[0-9a-zA-Z]*';
        },

        code() {
            return this.digits.join('');
        },

        isComplete() {
            return this.code.length === this.length && this.isValidCode(this.code);
        },

        calculatedSeparatorPosition() {
            if(this.separatorPosition !== null) {
                return this.separatorPosition;
            }

            // Return the separator position based on the code length. For example, for a length of 6, return 3.
            return Math.floor(this.length / 2);
        }
    },

    watch: {
        code(newCode) {
            this.$emit('input', newCode);
            this.$emit('change', newCode);

            if (this.isComplete) {
                this.$emit('complete', newCode);

                if (this.autoSubmit) {
                    this.$nextTick(() => {
                        this.$emit('submit', newCode);
                    });
                }
            }
        },

        value: {
            immediate: true,
            handler(newValue) {
                if (newValue && newValue !== this.code) {
                    this.setCode(newValue);
                }
            }
        }
    },

    mounted() {
        this.initializeDigits();

        // Focus first input on mount
        this.$nextTick(() => {
            this.focusInput(0);
        });
    },

    beforeDestroy() {
        if (this.shakeTimeout) {
            clearTimeout(this.shakeTimeout);
        }
        if (this.successTimeout) {
            clearTimeout(this.successTimeout);
        }
    },

    methods: {
        initializeDigits() {
            this.$set(this, 'digits', Array(this.length).fill(''));
        },

        isValidCode(code) {
            if (this.type === 'numeric') {
                return /^\d+$/.test(code);
            } else {
                return /^[0-9a-zA-Z]+$/.test(code);
            }
        },

        handleInput(index, event) {
            const value = event.target.value;

            // Clear shake/success states on new input
            this.clearAnimations();

            // Handle input based on type
            if (this.type === 'numeric') {
                // Only allow numeric input
                this.$set(this.digits, index, value.replace(/[^0-9]/g, ''));
            } else if (this.type === 'alphanumeric') {
                // Allow alphanumeric
                this.$set(this.digits, index, value.replace(/[^0-9a-zA-Z]/g, ''));
            } else {
                // Alphanumeric uppercase
                this.$set(this.digits, index, value.replace(/[^0-9a-zA-Z]/g, '').toUpperCase());
            }

            // Auto-advance to next input if filled
            if (this.digits[index] && index < this.length - 1) {
                this.$nextTick(() => {
                    this.focusInput(index + 1);
                });
            }
        },

        handleKeydown(index, event) {
            // Handle backspace
            if (event.key === 'Backspace') {
                event.preventDefault();

                if (this.digits[index]) {
                    // Clear current digit
                    this.$set(this.digits, index, '');
                } else if (index > 0) {
                    // Move to previous digit and clear it
                    this.focusInput(index - 1);
                    this.$set(this.digits, index - 1, '');
                }
            }
            // Handle delete key
            else if (event.key === 'Delete') {
                event.preventDefault();
                this.$set(this.digits, index, '');
            }
            // Handle arrow keys
            else if (event.key === 'ArrowLeft' && index > 0) {
                event.preventDefault();
                this.focusInput(index - 1);
            } else if (event.key === 'ArrowRight' && index < this.length - 1) {
                event.preventDefault();
                this.focusInput(index + 1);
            }
            // Handle home/end keys
            else if (event.key === 'Home') {
                event.preventDefault();
                this.focusInput(0);
            } else if (event.key === 'End') {
                event.preventDefault();
                this.focusInput(this.length - 1);
            }
        },

        handlePaste(event) {
            event.preventDefault();

            const pasteData = event.clipboardData.getData('text');
            let cleanedData = '';

            // Clean paste data based on type
            if (this.type === 'numeric') {
                cleanedData = pasteData.replace(/[^0-9]/g, '');
            } else {
                cleanedData = pasteData.replace(/[^0-9a-zA-Z]/g, '');
            }

            // Only use up to length characters
            const code = cleanedData.slice(0, this.length);

            if (code.length > 0) {
                this.setCode(code);

                // Focus the last filled input or the next empty one
                const focusIndex = Math.min(code.length, this.length - 1);
                this.$nextTick(() => {
                    this.focusInput(focusIndex);
                });
            }
        },

        handleFocus(index) {
            // Select all text in input when focused
            const input = this.$refs[`codeInput${index}`];
            if (input && input[0]) {
                this.$nextTick(() => {
                    input[0].select();
                });
            }

            this.$emit('focus', index);
        },

        focusInput(index) {
            const input = this.$refs[`codeInput${index}`];
            if (input && input[0]) {
                input[0].focus();
            }
        },

        setCode(code) {
            const cleanCode = code.slice(0, this.length);

            for (let i = 0; i < this.length; i++) {
                this.$set(this.digits, i, cleanCode[i] || '');
            }
        },

        clearAnimations() {
            this.isShaking = false;
            this.isSuccess = false;

            if (this.shakeTimeout) {
                clearTimeout(this.shakeTimeout);
            }
            if (this.successTimeout) {
                clearTimeout(this.successTimeout);
            }
        },

        // Public methods
        clear() {
            this.setCode('');
            this.clearAnimations();
            this.$nextTick(() => {
                this.focusInput(0);
            });
        },

        focus() {
            this.focusInput(0);
        },

        async showError(duration = 450) {
            this.clearAnimations();
            this.isShaking = true;

            this.shakeTimeout = setTimeout(() => {
                this.isShaking = false;
            }, duration);
        },

        async showSuccess(duration = 1000) {
            this.clearAnimations();
            this.isSuccess = true;

            this.successTimeout = setTimeout(() => {
                this.isSuccess = false;
            }, duration);
        },

        getCode() {
            return this.code;
        }
    }
};
</script>

<style lang="scss" scoped>
/* Shake animation for error state */
@keyframes error-shake {
    0% {
        transform: translateX(0);
    }
    20% {
        transform: translateX(-6px);
    }
    40% {
        transform: translateX(6px);
    }
    60% {
        transform: translateX(-4px);
    }
    80% {
        transform: translateX(4px);
    }
    100% {
        transform: translateX(0);
    }
}

/* Success animation */
@keyframes success-pulse {
    0% {
        border-color: #DDD;
    }
    50% {
        border-color: #2CC36B;
        box-shadow: 0 0 0 3px rgba(44, 195, 107, 0.2);
    }
    100% {
        border-color: #2CC36B;
    }
}

.code-input-wrapper {
    width: 100%;
}

.code-input-group {
    display: flex;
    width: 100%;
    align-items: center;
    flex-direction: row;
    gap: 0.3125rem;
    transition: opacity 0.2s ease, transform 0.2s ease;

    &.is-hidden {
        opacity: 0;
        transform: scale(0.95);
    }

    &.is-shaking .code-input {
        animation: error-shake 0.45s ease;
        border-color: #DE134E !important;
    }

    &.is-success .code-input {
        animation: success-pulse 0.6s ease;
        border-color: #2CC36B !important;
    }

    .code-input-separator {
        color: #DDD;
        text-align: center;
        font-size: 1.625rem;
        font-style: normal;
        font-weight: 400;
        width: 1.375rem;
        display: block;
        opacity: 0.65;
        user-select: none;
    }

    .code-input {
        text-align: center;
        font-size: 1.3125rem;
        font-weight: 600;
        flex: 1 0 0;
        height: 3.75rem;
        width: 0;
        padding: 0.5rem 0.6125rem;
        border-radius: 0.375rem;
        border: 1px solid #DDD;
        background: #FFF;
        transition: border-color 0.2s ease;
        box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);

        @media screen and (min-width: 769px) {
            padding: 0.5rem 0.8125rem;
        }

        &:focus {
            border-color: #6A7272;
            outline: none;
            box-shadow: 0 0 0 3px rgba(106, 114, 114, 0.1);
        }

        &:disabled {
            background-color: #F5F5F5;
            cursor: not-allowed;
            opacity: 0.7;
        }

        /* Remove spinner for number inputs */
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        &[type="number"] {
            -moz-appearance: textfield;
        }
    }
}
</style>
